/**
 * Header — ask bar left, range / branch / status / CTA right.
 *
 * The ask bar and "Test your assistant" are presentational in this build: there
 * is no search endpoint and nothing here may place a call. Both are marked
 * unavailable rather than wired to something that does not exist.
 *
 * The range is a real control. It was a static pill reading "Today · 13 Aug"
 * with no way to change it, while the screen below said "No calls in this
 * range" — naming a range the reader could neither identify nor alter.
 */
import { border, c, mono, radius } from '../theme/tokens';
import { Dot } from './primitives';
import { Lockup } from './Lockup';
import type { BranchSummary, DashboardContext, RangeKey, RangeOption } from '../api/types';

/** Shared shape for the two pill selects, so they cannot drift apart. */
function PillSelect<T extends string>({
  value,
  options,
  onChange,
  title,
  ariaLabel,
}: {
  value: T;
  options: { value: T; label: string }[];
  onChange: (next: T) => void;
  title?: string;
  ariaLabel: string;
}) {
  return (
    <div style={{ flex: '0 0 auto', position: 'relative', display: 'flex' }}>
      <select
        value={value}
        aria-label={ariaLabel}
        title={title}
        onChange={e => onChange(e.target.value as T)}
        className="vp-outline"
        style={{
          appearance: 'none',
          padding: '8px 34px 8px 14px',
          border: border.default,
          borderRadius: radius.pill,
          background: c.surface2,
          fontSize: 13.5,
          color: c.ink2,
          cursor: 'pointer',
        }}
      >
        {options.map(o => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      <span
        style={{
          position: 'absolute',
          right: 14,
          top: '50%',
          transform: 'translateY(-50%)',
          fontSize: 10,
          color: c.ink3,
          pointerEvents: 'none',
        }}
      >
        ▾
      </span>
    </div>
  );
}

export function Header({
  ctx,
  ranges,
  range,
  rangeLabel,
  lastUpdated,
  onRefresh,
  onRangeChange,
  onBranchChange,
  onTenantChange,
}: {
  ctx: DashboardContext;
  ranges: RangeOption[];
  range: RangeKey;
  /** Full label with dates, from the server, shown beside the control. */
  rangeLabel: string;
  lastUpdated: Date | null;
  onRefresh: () => void;
  onRangeChange: (next: RangeKey) => void;
  onBranchChange: (b: BranchSummary) => void;
  onTenantChange: (tenantId: string) => void;
}) {
  const live = ctx.assistant.live;
  const baseMessageOnly = !ctx.capabilities.cap_review_queue;
  const selectable = ranges.filter(r => r.key !== 'custom');

  return (
    <header
      style={{
        flex: '0 0 auto',
        background: 'rgba(255,255,255,0.82)',
        backdropFilter: 'blur(16px)',
        borderBottom: border.default,
        padding: '14px 32px',
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        flexWrap: 'wrap',
        rowGap: 10,
      }}
    >
      {/* Range — the label the whole screen is scoped by, and changeable. */}
      <div
        style={{
          marginLeft: 'auto',
          flex: '0 0 auto',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
        }}
      >
        <span style={{ fontFamily: mono, fontSize: 10, letterSpacing: '0.12em', color: c.ink3, whiteSpace: 'nowrap' }}>
          SHOWING
        </span>
        <PillSelect
          ariaLabel="Date range"
          title="Every figure on this screen covers the selected range."
          value={range}
          onChange={onRangeChange}
          options={selectable.map(r => ({ value: r.key, label: r.label }))}
        />
        <span style={{ fontSize: 12.5, color: c.ink3, whiteSpace: 'nowrap' }}>{rangeLabel}</span>
      </div>

      <button
        className="vp-reset vp-outline"
        onClick={onRefresh}
        title="Refresh now. The dashboard also checks for new calls every 10 seconds while this tab is open."
        style={{ padding: '8px 12px', border: border.default, borderRadius: radius.pill, background: c.surface2, color: c.ink2, fontSize: 12.5, cursor: 'pointer', whiteSpace: 'nowrap' }}
      >
        ↻ {lastUpdated ? `LIVE · ${lastUpdated.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}` : 'UPDATING'}
      </button>

      {/* ModelProp staff get a customer selector. It is absent from customer
          sessions entirely: the server omits the tenant list and rejects any
          tenantId a customer tries to add to a URL. */}
      {ctx.actor?.internal ? (
        <PillSelect
          ariaLabel="Customer"
          value={ctx.tenant.id}
          onChange={onTenantChange}
          options={ctx.actor.availableTenants.map(tenant => ({ value: tenant.id, label: tenant.name }))}
        />
      ) : null}

      {/* Branch switch — a real control, because the API is branch-scoped. */}
      {ctx.branches.length > 1 ? (
        <PillSelect
          ariaLabel="Branch"
          value={ctx.branch.id}
          onChange={id => {
            const next = ctx.branches.find(b => b.id === id);
            if (next) onBranchChange(next);
          }}
          options={ctx.branches.map(b => ({ value: b.id, label: b.name }))}
        />
      ) : null}

      <div
        style={{
          flex: '0 0 auto',
          display: 'flex',
          alignItems: 'center',
          gap: 7,
          padding: '8px 14px',
          borderRadius: radius.pill,
          background: live ? 'rgba(31,138,91,0.08)' : c.lineSoft,
        }}
      >
        <Dot color={live ? c.success : c.ink3} pulse={live} />
        <span
          style={{
            fontFamily: mono,
            fontSize: 10.5,
            letterSpacing: '0.08em',
            color: live ? c.success : c.ink3,
          }}
        >
          {live ? 'ASSISTANT LIVE' : 'NO NUMBER ATTACHED'}
        </span>
      </div>

      {!baseMessageOnly ? <button
        className="vp-reset"
        disabled
        title="Placing a test call is not part of this build — nothing here touches Retell or Twilio."
        style={{
          flex: '0 0 auto',
          whiteSpace: 'nowrap',
          padding: '10px 18px',
          borderRadius: radius.pill,
          background: c.lineSoft,
          color: c.ink3,
          border: border.default,
          fontSize: 13.5,
          cursor: 'not-allowed',
        }}
      >
        Test your assistant
      </button> : null}
      {/* Phones hide the rail, so the VoiceProp logo sits on its own row at the top here. */}
      <span className="vp-mobile-brand"><Lockup /></span>
    </header>
  );
}
